import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Building2, Mail, User, Calendar, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const INDUSTRIES = [
  "Financial Services", "Healthcare", "Technology", "Retail", "Manufacturing",
  "Energy & Utilities", "Government", "Education", "Telecommunications", "Other",
];

export default function Clients() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "assessor"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) =>
    [c.name, c.industry, c.contact_person, c.email].join(" ").toLowerCase().includes(search.toLowerCase())
  );

  const remove = async (id) => {
    if (!confirm("Delete this client and all its assessments?")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Client Organizations</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Clients</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage assessed organizations.</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setDialog({ open: true, item: null })}
            data-testid="new-client-button"
            className="bg-white text-black hover:bg-zinc-200"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New Client
          </Button>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, industry, contact..."
            data-testid="clients-search"
            className="pl-10 bg-[#121212] border-zinc-800 focus-visible:ring-zinc-400"
          />
        </div>
        <div className="text-xs text-zinc-500 mono">{filtered.length} / {items.length}</div>
      </div>

      <div className="mt-4 rounded-md border border-zinc-800 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm" data-testid="clients-empty">
            <Building2 className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            No clients found.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-uppercase">Organization</th>
                <th className="text-left px-4 py-3 label-uppercase">Industry</th>
                <th className="text-left px-4 py-3 label-uppercase">Contact</th>
                <th className="text-left px-4 py-3 label-uppercase">Email</th>
                <th className="text-left px-4 py-3 label-uppercase">Created</th>
                <th className="text-right px-4 py-3 label-uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/assessments?client=${c.id}`} className="text-white font-medium hover:underline" data-testid={`client-name-${c.id}`}>
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><Badge className="bg-zinc-900 text-zinc-300 border-zinc-800">{c.industry}</Badge></td>
                  <td className="px-4 py-3 text-zinc-300"><span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-zinc-500" />{c.contact_person}</span></td>
                  <td className="px-4 py-3 text-zinc-400 mono text-xs"><span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-zinc-500" />{c.email}</span></td>
                  <td className="px-4 py-3 text-zinc-500 mono text-xs"><Calendar className="w-3.5 h-3.5 inline mr-1" />{c.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setDialog({ open: true, item: c })}
                          data-testid={`edit-client-${c.id}`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white"
                        ><Pencil className="w-3.5 h-3.5" /></button>
                        <button
                          onClick={() => remove(c.id)}
                          data-testid={`delete-client-${c.id}`}
                          className="p-1.5 rounded hover:bg-red-500/10 text-zinc-400 hover:text-red-400"
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ClientDialog
        open={dialog.open}
        item={dialog.item}
        onClose={() => setDialog({ open: false, item: null })}
        onSaved={load}
      />
    </div>
  );
}

function ClientDialog({ open, item, onClose, onSaved }) {
  const editing = !!item;
  const [form, setForm] = useState({
    name: "", industry: INDUSTRIES[0], contact_person: "", email: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name || "", industry: item.industry || INDUSTRIES[0],
        contact_person: item.contact_person || "", email: item.email || "",
        notes: item.notes || "",
      });
    } else {
      setForm({ name: "", industry: INDUSTRIES[0], contact_person: "", email: "", notes: "" });
    }
  }, [item, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.patch(`/clients/${item.id}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-lg" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Client" : "New Client"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Organization name">
            <Input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-zinc-800" />
          </Field>
          <Field label="Industry">
            <select
              value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
              data-testid="client-industry-select"
              className="w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm text-white"
            >
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact person">
              <Input data-testid="client-contact-input" required value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="bg-[#0A0A0A] border-zinc-800" />
            </Field>
            <Field label="Contact email">
              <Input data-testid="client-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-zinc-800" />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea data-testid="client-notes-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-zinc-800" />
          </Field>
          <DialogFooter className="gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="client-submit-button" className="bg-white text-black hover:bg-zinc-200">
              {busy ? "Saving…" : editing ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-uppercase">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
